/* M6.7 — COD Cycle Config. Source of truth for Expected Remittance Date. Stored
   at courier-code level (parent default allowed), pre-populated from onboarding
   COD SLA. Add / Edit via a drawer; edits are effective-dated and require a
   reason that is recorded in the audit log (old → new). */
import React, {useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import CheckIcon from '@mui/icons-material/Check'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import {SupplyTable, SideDrawer, SearchSelect, useToast, fmtDate, agreementLabel, agreementShort} from '../components'
import {VerticalChip, MiniPill} from './codBits'
import {
  visibleConfig,
  COD_CONFIG,
  WEEKDAYS,
  DUMMY_CODES,
  AGREEMENTS,
  visibleVerticals,
  slaFor,
  TODAY,
} from './codLogic'

function AgreementCell({id}) {
  if (!id)
    return (
      <MiniPill tone='amber'>
        <WarningAmberOutlinedIcon style={{width: 11, height: 11, verticalAlign: -1}} /> Unlinked
      </MiniPill>
    )
  return (
    <span className='tag' title={agreementLabel(id)}>
      <DescriptionOutlinedIcon style={{width: 12, height: 12, verticalAlign: -2}} /> {agreementShort(id)}
    </span>
  )
}
AgreementCell.propTypes = {id: PropTypes.string}

export default function ConfigTab() {
  const toast = useToast()
  const [, force] = useState(0)
  const refresh = () => force((n) => n + 1)
  const [editor, setEditor] = useState(null) // {cfg} | {} (new)

  const rows = visibleConfig()

  const columns = [
    {
      key: 'code',
      label: 'Courier Code',
      render: (c) => (
        <span className='mono' style={{fontSize: 12}}>
          {c.code}
        </span>
      ),
    },
    {key: 'vertical', label: 'Vertical', render: (c) => <VerticalChip vertical={c.vertical} />},
    {key: 'cycle', label: 'Cycle', align: 'center', render: (c) => <b>D+{c.cycle}</b>, value: (c) => c.cycle},
    {
      key: 'weekdays',
      label: 'Settlement Weekdays',
      sortable: false,
      render: (c) =>
        (c.weekdays || []).length ? (
          <span className='row wrap gap8' style={{display: 'inline-flex'}}>
            {c.weekdays.map((w) => (
              <span className='tag chip-blue' key={w}>
                {w}
              </span>
            ))}
          </span>
        ) : (
          <span className='muted'>—</span>
        ),
    },
    {
      key: 'cutoff',
      label: 'Daily Cut-off',
      align: 'center',
      render: (c) => <span className='mono'>{c.cutoff}</span>,
    },
    {key: 'effectiveFrom', label: 'Effective From', render: (c) => fmtDate(c.effectiveFrom), value: (c) => c.effectiveFrom},
    {
      key: 'effectiveTo',
      label: 'Effective To',
      render: (c) => (c.effectiveTo ? fmtDate(c.effectiveTo) : <MiniPill tone='green'>Open-ended</MiniPill>),
      value: (c) => c.effectiveTo || '',
    },
    {
      key: 'linkedAgreement',
      label: 'Linked Agreement',
      sortable: false,
      render: (c) => <AgreementCell id={c.linkedAgreement} />,
    },
    {
      key: 'notes',
      label: 'Notes',
      sortable: false,
      render: (c) =>
        c.notes ? (
          <span
            className='muted'
            title={c.notes}
            style={{
              fontSize: 12,
              display: 'inline-block',
              maxWidth: 220,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              verticalAlign: 'bottom',
            }}
          >
            {c.notes}
          </span>
        ) : (
          <span className='muted'>—</span>
        ),
    },
  ]

  const rowActions = (c) => [
    {label: 'Edit config', icon: <EditOutlinedIcon fontSize='small' />, onClick: () => setEditor({cfg: c})},
  ]

  return (
    <div>
      <div className='page-head' style={{marginBottom: 14}}>
        <div>
          <div className='seclabel' style={{marginBottom: 4}}>
            COD Cycle Config — source of truth for Expected Remittance Date
          </div>
          <p className='sub' style={{margin: 0}}>
            Stored at courier-code level (parent default allowed). Pre-populated from onboarding COD
            SLA. Changes are effective-dated and audited.
          </p>
        </div>
        <button className='btn btn-primary' onClick={() => setEditor({cfg: null})}>
          <AddIcon style={{width: 16, height: 16}} />
          Add Config
        </button>
      </div>

      <div className='warnbox info' style={{marginBottom: 14}}>
        <span style={{display: 'flex'}}>
          <InfoOutlinedIcon style={{width: 16, height: 16}} />
        </span>
        <div>
          Historical AWBs reconcile against the config active on their delivered date. If no
          courier-code config exists, the system falls back to the parent courier config; if none, it
          shows <b>Cycle config missing</b> and blocks overdue classification.
        </div>
      </div>

      <SupplyTable
        rows={rows}
        rowKey={(c) => c.code + c.effectiveFrom}
        columns={columns}
        searchKeys={['code', 'vertical', 'linkedAgreement', 'notes']}
        searchPlaceholder='Search courier code, vertical...'
        exportName='cod-cycle-config'
        emptyText='No cycle configs in scope.'
        defaultSort={{key: 'code', dir: 'asc'}}
        rowActions={rowActions}
      />

      <ConfigEditor
        ctx={editor}
        onClose={() => setEditor(null)}
        onSaved={() => {
          setEditor(null)
          refresh()
        }}
        toast={toast}
      />
    </div>
  )
}

function ConfigEditor({ctx, onClose, onSaved, toast}) {
  const open = !!ctx
  const cfg = ctx ? ctx.cfg : null
  const isNew = open && !cfg

  const codes = useMemo(() => {
    const v = visibleVerticals()
    return DUMMY_CODES.filter((c) => v.indexOf(c.vertical) >= 0)
  }, [])
  const agrOpts = AGREEMENTS.map((a) => a.id)

  const [model, setModel] = useState({})
  const [errs, setErrs] = useState({})

  React.useEffect(() => {
    if (!open) return
    setErrs({})
    setModel({
      code: cfg ? cfg.code : '',
      vertical: cfg ? cfg.vertical : '',
      cycle: cfg ? String(cfg.cycle) : '',
      weekdays: cfg ? (cfg.weekdays || []).slice() : [],
      cutoff: cfg ? cfg.cutoff : '12:00',
      effectiveFrom: cfg ? cfg.effectiveFrom : TODAY.toISOString().slice(0, 10),
      effectiveTo: cfg ? cfg.effectiveTo || '' : '',
      linkedAgreement: cfg ? cfg.linkedAgreement || '' : '',
      notes: cfg ? cfg.notes || '' : '',
      reason: '',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const set = (k, v) => setModel((m) => ({...m, [k]: v}))
  const clearErr = (k) => setErrs((e) => ({...e, [k]: false}))

  const onCodeChange = (code) => {
    const cc = DUMMY_CODES.find((x) => x.code === code)
    let cycle = model.cycle
    if (cc) {
      const sla = slaFor(code)
      if (sla != null && !model.cycle) {
        cycle = String(sla)
        toast('Cycle pre-populated to D+' + sla + ' from onboarding COD SLA.', 'info')
      }
    }
    setModel((m) => ({...m, code, vertical: cc ? cc.vertical : '', cycle}))
    clearErr('code')
  }

  const toggleWeekday = (w) => {
    setModel((m) => {
      const has = m.weekdays.indexOf(w) >= 0
      const wk = has ? m.weekdays.filter((x) => x !== w) : m.weekdays.concat([w])
      return {...m, weekdays: wk}
    })
    clearErr('weekdays')
  }

  const save = () => {
    const e = {}
    if (!model.code) e.code = true
    if (model.cycle === '') e.cycle = true
    if (!model.weekdays.length) e.weekdays = true
    if (!model.effectiveFrom) e.effectiveFrom = true
    if (!isNew && !model.reason.trim()) e.reason = true
    if (Object.keys(e).length) {
      setErrs(e)
      toast(e.reason && Object.keys(e).length === 1 ? 'Enter a reason for the change.' : 'Complete the required fields.', 'error')
      return
    }
    const vertical = (DUMMY_CODES.find((x) => x.code === model.code) || {}).vertical || model.vertical
    const rec = {
      code: model.code,
      vertical,
      cycle: +model.cycle,
      weekdays: model.weekdays.slice().sort((a, b) => WEEKDAYS.indexOf(a) - WEEKDAYS.indexOf(b)),
      cutoff: model.cutoff || '12:00',
      effectiveFrom: model.effectiveFrom,
      effectiveTo: model.effectiveTo || null,
      linkedAgreement: model.linkedAgreement || null,
      notes: model.notes || '',
    }
    if (isNew) {
      COD_CONFIG.push(rec)
      toast(
        'Cycle config added for ' + rec.code + '. It is now the source of truth for Expected Remittance Date.',
        'success'
      )
    } else {
      Object.keys(rec).forEach((k) => {
        cfg[k] = rec[k]
      })
      toast('Cycle config updated for ' + rec.code + '. Audit recorded.', 'success')
    }
    onSaved()
  }

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title={(isNew ? 'Add' : 'Edit') + ' Cycle Config'}
      width={540}
      footer={
        <>
          <button className='btn' onClick={onClose}>
            Cancel
          </button>
          <button className='btn btn-primary' onClick={save}>
            <CheckIcon style={{width: 16, height: 16}} />
            {isNew ? 'Add Config' : 'Save Changes'}
          </button>
        </>
      }
    >
      <div className={'field' + (errs.code ? ' invalid' : '')}>
        <label>
          Courier / courier code <span className='req'>*</span>
        </label>
        {isNew ? (
          <SearchSelect
            value={model.code || ''}
            invalid={errs.code}
            placeholder='Search a courier code…'
            options={codes.map((c) => ({value: c.code, label: c.parent + ' · ' + c.code + ' (' + c.vertical + ')'}))}
            onChange={(v) => onCodeChange(v)}
          />
        ) : (
          <input className='input' value={model.code || ''} disabled />
        )}
        <div className='help'>
          <InfoOutlinedIcon style={{width: 13, height: 13}} />
          Config is stored at courier-code level, with a parent default allowed.
        </div>
        <div className='vmsg'>Select a courier code.</div>
      </div>

      <div className='row gap12'>
        <div className='field' style={{flex: 1}}>
          <label>Vertical</label>
          <input className='input' value={model.vertical || ''} disabled placeholder='Auto from code' />
        </div>
        <div className={'field' + (errs.cycle ? ' invalid' : '')} style={{flex: 1}}>
          <label>
            Cycle (D+N) <span className='req'>*</span>
          </label>
          <input
            className={'input' + (errs.cycle ? ' err' : '')}
            type='number'
            min='0'
            value={model.cycle || ''}
            placeholder='e.g. 7'
            onChange={(e) => {
              set('cycle', e.target.value)
              clearErr('cycle')
            }}
          />
          <div className='help'>
            <InfoOutlinedIcon style={{width: 13, height: 13}} />
            Pre-populated from onboarding COD SLA.
          </div>
          <div className='vmsg'>Enter cycle days.</div>
        </div>
      </div>

      <div className={'field' + (errs.weekdays ? ' invalid' : '')}>
        <label>
          Settlement weekdays <span className='req'>*</span>
        </label>
        <div className='row wrap gap8'>
          {WEEKDAYS.map((w) => (
            <label className='chip' style={{cursor: 'pointer'}} key={w}>
              <input
                type='checkbox'
                checked={(model.weekdays || []).indexOf(w) >= 0}
                onChange={() => toggleWeekday(w)}
              />
              {w}
            </label>
          ))}
        </div>
        <div className='help'>
          <InfoOutlinedIcon style={{width: 13, height: 13}} />
          Monday–Sunday. Selecting all 7 means daily settlement.
        </div>
        <div className='vmsg'>Select at least one settlement weekday.</div>
      </div>

      <div className='row gap12'>
        <div className={'field' + (errs.cutoff ? ' invalid' : '')} style={{flex: 1}}>
          <label>
            Daily cut-off <span className='req'>*</span>
          </label>
          <input
            className='input'
            type='time'
            value={model.cutoff || ''}
            onChange={(e) => set('cutoff', e.target.value)}
          />
          <div className='help'>
            <InfoOutlinedIcon style={{width: 13, height: 13}} />
            Used in the Expected Remittance Date computation.
          </div>
        </div>
        <div className='field' style={{flex: 1}}>
          <label>
            Linked agreement <span className='opt'>(optional)</span>
          </label>
          <SearchSelect
            value={model.linkedAgreement || ''}
            placeholder='— none —'
            options={agrOpts}
            onChange={(v) => set('linkedAgreement', v)}
          />
        </div>
      </div>

      <div className='row gap12'>
        <div className={'field' + (errs.effectiveFrom ? ' invalid' : '')} style={{flex: 1}}>
          <label>
            Effective from <span className='req'>*</span>
          </label>
          <input
            className={'input' + (errs.effectiveFrom ? ' err' : '')}
            type='date'
            value={model.effectiveFrom || ''}
            onChange={(e) => {
              set('effectiveFrom', e.target.value)
              clearErr('effectiveFrom')
            }}
          />
          <div className='help'>
            <InfoOutlinedIcon style={{width: 13, height: 13}} />
            Historical AWBs use the config active at the time.
          </div>
          <div className='vmsg'>Pick an effective-from date.</div>
        </div>
        <div className='field' style={{flex: 1}}>
          <label>
            Effective to <span className='opt'>(optional)</span>
          </label>
          <input
            className='input'
            type='date'
            value={model.effectiveTo || ''}
            onChange={(e) => set('effectiveTo', e.target.value)}
          />
          <div className='help'>
            <InfoOutlinedIcon style={{width: 13, height: 13}} />
            Blank means active / open-ended.
          </div>
        </div>
      </div>

      <div className='field'>
        <label>
          Notes <span className='opt'>(optional)</span>
        </label>
        <textarea
          className='input'
          rows={2}
          value={model.notes || ''}
          placeholder='Internal comments'
          onChange={(e) => set('notes', e.target.value)}
        />
      </div>

      {!isNew ? (
        <>
          <div className={'field' + (errs.reason ? ' invalid' : '')}>
            <label>
              Reason for change <span className='req'>*</span>
            </label>
            <textarea
              className={'input' + (errs.reason ? ' err' : '')}
              rows={2}
              value={model.reason || ''}
              placeholder='e.g. Revised remittance terms in FY27 addendum'
              onChange={(e) => {
                set('reason', e.target.value)
                clearErr('reason')
              }}
            />
            <div className='help'>
              <InfoOutlinedIcon style={{width: 13, height: 13}} />
              Recorded in the audit log with your name, timestamp and the old → new values.
            </div>
            <div className='vmsg'>A reason is required for config changes.</div>
          </div>
          <div className='warnbox info' style={{marginTop: 4}}>
            <span style={{display: 'flex'}}>
              <InfoOutlinedIcon style={{width: 16, height: 16}} />
            </span>
            <div>
              Editing records actioned_by, actioned_at, old value, new value and a reason. Changes are
              effective-dated.
            </div>
          </div>
        </>
      ) : null}
    </SideDrawer>
  )
}
ConfigEditor.propTypes = {
  ctx: PropTypes.object,
  onClose: PropTypes.func,
  onSaved: PropTypes.func,
  toast: PropTypes.func,
}
